import LegalPageLayout from './LegalPageLayout';
import {
	LegalContact,
	LegalEffectiveDate,
	LegalList,
	LegalParagraph,
	LegalSection,
} from './LegalSection';

const Terms = () => (
	<LegalPageLayout title="Terms of Service">
		<LegalEffectiveDate />

		<LegalParagraph>
			These Terms of Service (&quot;Terms&quot;) govern access to and use of
			Bidzbase (the &quot;Service&quot;) at bidzbase.com and related
			applications. By creating an account, starting a trial, or using the
			Service, you agree to these Terms on behalf of yourself and the business
			(&quot;Organization&quot;) you represent.
		</LegalParagraph>

		<LegalSection title="1. The Service">
			<LegalParagraph>
				Bidzbase is a multi-tenant business software platform for subcontractors
				and trade businesses. Organizations use the Service to manage customers,
				jobs, bids, proposals, invoices, files, and team members. Each
				Organization&apos;s data is isolated from other Organizations.
			</LegalParagraph>
			<LegalParagraph>
				We may update, improve, or discontinue features over time. We will use
				reasonable efforts to avoid material adverse changes to paid features
				you rely on during an active subscription term.
			</LegalParagraph>
		</LegalSection>

		<LegalSection title="2. Accounts and eligibility">
			<LegalParagraph>
				You must be at least 18 years old and authorized to bind the
				Organization you register. Account credentials must be kept
				confidential. You are responsible for activity under your account.
			</LegalParagraph>
			<LegalParagraph>
				Organizations may invite team members with assigned roles (for example,
				Manager or Employee). The Organization is responsible for managing
				access and permissions for its users.
			</LegalParagraph>
		</LegalSection>

		<LegalSection title="3. Subscriptions, billing, and trials">
			<LegalParagraph>
				New Organizations start on a 30-day free trial with no credit card
				required. When the trial ends, you must choose a paid plan (Basic or
				Pro) or deactivate the Organization. We do not automatically charge
				you at the end of a trial.
			</LegalParagraph>
			<LegalParagraph>
				Paid plans are billed monthly through Stripe on the first day of each
				month (America/Denver). If you start a paid plan on any other day, the
				first invoice is prorated through the next first of the month. By
				starting a paid subscription, you authorize us and Stripe to charge
				your payment method on a recurring basis.
			</LegalParagraph>
			<LegalList
				items={[
					'One free trial is allowed per phone number. We retain signup phone numbers to enforce this limit.',
					'Plan limits (such as feature access) depend on the tier you select after the trial. Some trial features are Pro-only after conversion.',
					'Upgrades may take effect immediately and may be prorated. Downgrades generally take effect at the end of the current billing period.',
					'We may change standard pricing for new customers. We will provide notice before price changes apply to your existing subscription.',
				]}
			/>
			<LegalParagraph>
				Fees are non-refundable except where required by law or explicitly
				stated otherwise.
			</LegalParagraph>
		</LegalSection>

		<LegalSection title="4. Stripe Connect and customer payments">
			<LegalParagraph>
				Organizations may optionally connect a Stripe account to collect
				payments from their own customers through invoices and payment links.
				When you use Stripe Connect:
			</LegalParagraph>
			<LegalList
				items={[
					"You enter into a separate relationship with Stripe and must comply with Stripe's terms and applicable payment laws.",
					'Bidzbase is not a party to transactions between your Organization and your customers.',
					'Your Organization is responsible for pricing, taxes, refunds, chargebacks, and customer payment disputes.',
					'We may disable payment features if we detect abuse, fraud, or material violation of these Terms.',
				]}
			/>
		</LegalSection>

		<LegalSection title="5. Your data and customer data">
			<LegalParagraph>
				You retain ownership of content you upload or submit to the Service,
				including customer records, job details, bids, line items, files, and
				branding assets.
			</LegalParagraph>
			<LegalParagraph>
				You grant Bidzbase a limited license to host, process, transmit, and
				display that content solely to operate and improve the Service (for
				example, generating PDFs, sending email on your behalf, and storing
				files).
			</LegalParagraph>
			<LegalParagraph>
				For personal information about your customers and employees that you
				enter into Bidzbase, your Organization is the business responsible for
				that data. You must have a lawful basis to collect and use it, provide
				any required notices, and honor applicable privacy rights.
			</LegalParagraph>
		</LegalSection>

		<LegalSection title="6. Acceptable use">
			<LegalParagraph>You agree not to:</LegalParagraph>
			<LegalList
				items={[
					'Use the Service for unlawful, fraudulent, or harmful activity.',
					'Send spam, unsolicited marketing, or messages that violate anti-spam or telecommunications laws.',
					"Attempt to access another Organization's data or probe, scan, or test system vulnerabilities without authorization.",
					'Reverse engineer, resell, or sublicense the Service except as expressly permitted.',
					'Upload malware or content that infringes intellectual property or privacy rights.',
				]}
			/>
			<LegalParagraph>
				We may suspend or terminate access for violations, security risks, or
				non-payment.
			</LegalParagraph>
		</LegalSection>

		<LegalSection title="7. Customer proposals and electronic acceptance">
			<LegalParagraph>
				When your Organization sends proposals through the Service, customers
				may accept or decline by email link without creating an account. By
				using these features, you authorize Bidzbase to record customer
				responses, timestamps, and basic technical metadata (such as IP address
				and browser user agent) on your behalf.
			</LegalParagraph>
			<LegalList
				items={[
					'You are solely responsible for the accuracy, legality, and completeness of proposal content, pricing, scope, and terms you send to customers.',
					'When a customer clicks Accept, the Service records acceptance immediately and may offer a short cancellation window configured in your Organization settings.',
					'If the customer does not cancel within that window, acceptance is treated as binding between your Organization and the customer under applicable electronic-signature laws, subject to your proposal terms.',
					'Bidzbase provides software to facilitate acceptance and record-keeping; we do not guarantee enforceability of any particular contract and we are not a party to agreements between your Organization and your customers.',
				]}
			/>
		</LegalSection>

		<LegalSection title="8. Email and SMS">
			<LegalParagraph>
				The Service sends transactional email on your Organization&apos;s behalf
				(for example, proposals, invoices, reminders, and accept/decline
				notifications). You are responsible for the accuracy of recipient
				addresses and for complying with laws that apply to your communications.
			</LegalParagraph>
			<LegalParagraph>
				If SMS features are available on your plan, they are intended for
				internal, transactional manager notifications only - not marketing
				blasts. Managers must opt in via the Settings SMS alerts form (or the
				documented public SMS opt-in flow), including an affirmative, unticked
				consent checkbox. Message frequency varies. Msg &amp; data rates may
				apply. Recipients can reply STOP to cancel or HELP for help. You must
				comply with laws that apply to your communications, including TCPA
				requirements in the United States.
			</LegalParagraph>
		</LegalSection>

		<LegalSection title="9. Intellectual property">
			<LegalParagraph>
				Bidzbase and its logos, software, and documentation are owned by us or
				our licensors. These Terms do not grant you any rights to our trademarks
				or platform code except the limited right to use the Service during an
				active subscription.
			</LegalParagraph>
		</LegalSection>

		<LegalSection title="10. Third-party services">
			<LegalParagraph>
				The Service integrates with third parties such as Stripe (billing and
				payments), SendGrid (email delivery), Amazon Web Services (file
				storage), and Sentry (error monitoring). Your use of those services may
				be subject to their own terms and privacy policies.
			</LegalParagraph>
		</LegalSection>

		<LegalSection title="11. Disclaimers">
			<LegalParagraph>
				THE SERVICE IS PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE.&quot;
				TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES,
				EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR
				PURPOSE, AND NON-INFRINGEMENT. WE DO NOT GUARANTEE UNINTERRUPTED OR
				ERROR-FREE OPERATION.
			</LegalParagraph>
		</LegalSection>

		<LegalSection title="12. Limitation of liability">
			<LegalParagraph>
				TO THE MAXIMUM EXTENT PERMITTED BY LAW, BIDZBASE WILL NOT BE LIABLE FOR
				ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES,
				OR FOR LOST PROFITS, REVENUE, DATA, OR GOODWILL. OUR TOTAL LIABILITY FOR
				ANY CLAIM RELATING TO THE SERVICE IS LIMITED TO THE AMOUNTS YOU PAID TO
				BIDZBASE FOR THE SERVICE IN THE TWELVE (12) MONTHS BEFORE THE EVENT
				GIVING RISE TO THE CLAIM.
			</LegalParagraph>
		</LegalSection>

		<LegalSection title="13. Indemnification">
			<LegalParagraph>
				You will defend and indemnify Bidzbase against claims, damages, and
				expenses (including reasonable legal fees) arising from your content,
				your customer relationships, your use of Stripe Connect, your
				communications sent through the Service, or your violation of these
				Terms or applicable law.
			</LegalParagraph>
		</LegalSection>

		<LegalSection title="14. Termination and data retention">
			<LegalParagraph>
				You may cancel your subscription at any time. Cancellation stops future
				billing and access continues through the end of the current paid period
				unless we terminate sooner for cause.
			</LegalParagraph>
			<LegalParagraph>
				After cancellation or termination, we retain Organization data for
				thirty (30) days to allow export, then delete it unless we must retain
				certain records for legal, security, or billing purposes.
			</LegalParagraph>
		</LegalSection>

		<LegalSection title="15. Changes to these Terms">
			<LegalParagraph>
				We may update these Terms from time to time. If changes are material, we
				will provide notice (for example, by email or in-app notice) before they
				take effect. Continued use after the effective date constitutes
				acceptance of the updated Terms.
			</LegalParagraph>
		</LegalSection>

		<LegalSection title="16. General">
			<LegalList
				items={[
					'These Terms are the entire agreement between you and Bidzbase regarding the Service.',
					'If any provision is unenforceable, the remaining provisions remain in effect.',
					'You may not assign these Terms without our consent. We may assign them in connection with a merger, acquisition, or sale of assets.',
					'These Terms are governed by the laws of the United States and the State of Colorado, excluding conflict-of-law rules.',
				]}
			/>
		</LegalSection>

		<LegalContact subject="Terms of Service inquiry" />
	</LegalPageLayout>
);

export default Terms;
